import {
  PUBLIC_INDEX_ROBOTS,
  PUBLIC_NOINDEX_ROBOTS,
  resolvePublicRobots,
} from "./publicIndexablePaths";

const BRAND_SUFFIX = " | Folyamat";
const INDEX_ROBOTS = PUBLIC_INDEX_ROBOTS;
const NOINDEX_ROBOTS = PUBLIC_NOINDEX_ROBOTS;
const DEFAULT_OG_IMAGE_PATH = "/folyamat-social-share-img.webp";
const TITLE_BRAND_LIMIT = 60;
const DESCRIPTION_LIMIT = 160;
export const BLOG_POST_PATH_PREFIX = "/p/";
const EVENT_PATH_PREFIX = "/events/";
const EVENT_APP_PATH_PREFIX = "/events-app/";
const COURSE_PATH_PREFIX = "/courses/";
const PROFILE_PATH_PREFIX = "/profilom";
const ARAMLAS_PATH_PREFIX = "/aramlas";
const AUTH_ROUTE_PREFIXES = ["/admin"];
const PRIVATE_ADMIN_SEO_PREFIXES = [
  ...AUTH_ROUTE_PREFIXES,
  "/aramlas/admin",
];

export const ADMIN_APP_METADATA = {
  title: "Admin | Folyamat",
  description: "Folyamat admin felület — nem nyilvános oldal.",
  ogTitle: "Admin | Folyamat",
  ogDescription: "Folyamat admin felület — nem nyilvános oldal.",
} as const;

export const CANONICAL_SITE_URL = "https://www.folyamat.com";
export const BLOG_COLLECTION_PATH = "/irasok";
export const BLOG_COLLECTION_DESCRIPTION =
  "Az igazság mindenkinek jár — az Írások segítenek közeledni.";
export const BLOG_COLLECTION_OG_IMAGE_PATH = "/images/irasok-social-share-img.webp";
export const BLOG_COLLECTION_OG_IMAGE_ALT = "Írások – Az igazság mindenkinek jár";

export type SeoType = "website" | "article" | "event";

export interface SeoMetadata {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  canonicalPath: string;
  imageUrl: string;
  robots: string;
  type: SeoType;
  twitterCard: "summary_large_image";
  publishedTime?: string;
}

type PartialSeoMetadata = Partial<Omit<SeoMetadata, "twitterCard" | "imageUrl">> & {
  imageUrl?: string | null;
};

type RouteMatcher = {
  test: RegExp;
  resolve: (pathname: string) => PartialSeoMetadata;
};

type PostSeoInput = {
  slug: string;
  title: string;
  excerpt?: string | null;
  metaDescription?: string | null;
  imageUrl?: string | null;
  publishedAt?: string | null;
};

type EventSeoInput = {
  slugOrId: string;
  title: string;
  description?: string | null;
  eventDate?: string | null;
  imageUrl?: string | null;
  canonicalPath?: string;
};

type CourseSeoInput = {
  slug: string;
  title: string;
  shortDescription?: string | null;
  description?: string | null;
  imageUrl?: string | null;
};

export const FALLBACK_METADATA = {
  title: "Folyamat",
  description:
    "Krisztusi Folyamat azoknak, akik nem újabb módszert keresnek, hanem élő kapcsolatot Istennel.",
  ogTitle: "Folyamat",
  ogDescription:
    "Élő hit alapú tér napi tartalmakkal azért, hogy megismerd a Szent Szellem valóságos erejét!",
} as const;

export const HOME_METADATA = {
  title: "Visszatérés a Forráshoz | Folyamat",
  description:
    "Krisztusi Folyamat azoknak, akik nem újabb módszert keresnek, hanem élő kapcsolatot Istennel.",
  ogTitle: "Visszatérés a Forráshoz | Folyamat",
  ogDescription:
    "Kiszállás az ördögi körökből, és megérkezés az Életbe, amibe eredetileg születtél.",
} as const;

const STATIC_ROUTE_METADATA: Record<string, PartialSeoMetadata> = {
  "/": {
    ...HOME_METADATA,
    canonicalPath: "/start",
    robots: NOINDEX_ROBOTS,
  },
  "/home": {
    ...HOME_METADATA,
    canonicalPath: "/start",
    robots: NOINDEX_ROBOTS,
  },
  "/mentor": {
    title: "Mentor | Folyamat",
    description:
      "A Mentor segít eligazodni a Folyamat tartalmaiban és megtalálni a következő tiszta lépést.",
    canonicalPath: "/mentor",
    robots: NOINDEX_ROBOTS,
  },
  "/gabor": {
    title: "Gürtler Gábor | Folyamat",
    description: "Gürtler Gábor története és a Folyamat mögötti személyes út.",
    canonicalPath: "/gabor",
    robots: NOINDEX_ROBOTS,
  },
  "/kuldetes": {
    title: "Küldetés | Folyamat",
    description: "A Folyamat küldetése és annak a térnek a szándéka, amelyet képvisel.",
    canonicalPath: "/kuldetes",
    robots: NOINDEX_ROBOTS,
  },
  "/rolunk": {
    title: "Rólunk | Folyamat",
    description: "Kik vagyunk, hogyan született a Folyamat, és milyen térbe hívunk.",
    canonicalPath: "/rolunk",
    robots: NOINDEX_ROBOTS,
  },
  "/alapok": {
    title: "Új Alapok – 6 hetes út Isten jelenlétébe | Folyamat",
    description:
      "6 hetes vezetett online út azoknak, akik már sokat tudnak, de érzik: valami hiányzik. Élő kapcsolat Istennel — Krisztus által. Kezdés hétfőnként 18:00.",
    canonicalPath: "/alapok",
    robots: NOINDEX_ROBOTS,
  },
  "/alapok/sikeres": {
    title: "Sikeres jelentkezés – Új Alapok | Folyamat",
    description: "Köszönjük a jelentkezésedet az Új Alapok 6 hetes útra.",
    canonicalPath: "/alapok/sikeres",
    robots: "noindex,nofollow",
  },
  "/bekapcs": {
    title: "Bekapcsolódás – vezetett örökzöld anyag | Folyamat",
    description:
      "Vezetett Folyamat-anyag azoknak, akik már sokat tudnak, de mégis hiányzik valami. Felvételek, napi megerősítések — saját tempóban, bármikor.",
    canonicalPath: "/bekapcs",
  },
  "/bekapcs/sikeres": {
    title: "Sikeres vásárlás – Bekapcsolódás | Folyamat",
    description:
      "Sikeresen hozzájutottál a Bekapcsolódás anyaghoz. Nézd az email fiókodat — ott találod a tartalom linkjét.",
    canonicalPath: "/bekapcs/sikeres",
    robots: "noindex,nofollow",
  },
  "/taska": {
    title: "Folyamat táska | Folyamat",
    description: "Prémium pamut táska — vidd magaddal a Folyamatod mindenhová.",
    canonicalPath: "/taska",
  },
  "/taska/sikeres": {
    title: "Sikeres rendelés – Folyamat táska | Folyamat",
    description: "Köszönjük a rendelésedet. Hamarosan emailben megerősítjük, és feladjuk a táskákat.",
    canonicalPath: "/taska/sikeres",
    robots: "noindex,nofollow",
  },
  "/kozosseg": {
    title: "Közösség | Folyamat",
    description: "Kapcsolódás, megosztás és közös haladás a Folyamat közösségében.",
    canonicalPath: "/kozosseg",
    robots: NOINDEX_ROBOTS,
  },
  "/ujdonsagok": {
    title: "Újdonságok | Folyamat",
    description: "Friss hírek, események és új tartalmak a Folyamat oldalán.",
    canonicalPath: "/ujdonsagok",
    robots: NOINDEX_ROBOTS,
  },
  "/ingyenes-tartalmak": {
    title: "Ingyenes tartalmak | Folyamat",
    description: "Szabadon elérhető írások, hanganyagok és belépési pontok a Folyamatban.",
    canonicalPath: "/ingyenes-tartalmak",
    robots: NOINDEX_ROBOTS,
  },
  "/events": {
    title: "Események | Folyamat",
    description: "Közelgő események, találkozások és alkalmak a Folyamat oldalán.",
    canonicalPath: "/events",
  },
  [BLOG_COLLECTION_PATH]: {
    title: "Írások | Folyamat",
    description: BLOG_COLLECTION_DESCRIPTION,
    canonicalPath: BLOG_COLLECTION_PATH,
    imageUrl: BLOG_COLLECTION_OG_IMAGE_PATH,
  },
  "/courses": {
    title: "Tanfolyamok | Folyamat",
    description: "Publikus tanfolyamok és programok a Folyamat oldalán.",
    canonicalPath: "/courses",
    robots: NOINDEX_ROBOTS,
  },
  "/archivum": {
    title: "Archívum | Folyamat",
    description:
      "600+ hang- és videótartalom a Folyamat első három és fél évéből, egyetlen Archívumban.",
    canonicalPath: "/archivum",
  },
  "/kapcsolat": {
    title: "Kapcsolat | Folyamat",
    description: "Írj nekünk emailt vagy hívj minket telefonon. Szívesen segítünk.",
    canonicalPath: "/kapcsolat",
  },
  "/kapcsolattartasi-adatok": {
    title: "Kapcsolattartási adatok | Folyamat",
    description: "Kapcsolattartási és szolgáltatói adatok a Folyamat oldalán.",
    canonicalPath: "/kapcsolattartasi-adatok",
  },
  "/adatvedelem": {
    title: "Adatvédelem | Folyamat",
    description: "A Folyamat adatkezelési és adatvédelmi tájékoztatója.",
    canonicalPath: "/adatvedelem",
  },
  "/sutik": {
    title: "Sütikezelési tájékoztató | Folyamat",
    description: "A Folyamat webhely cookie-kategóriái és beállítási lehetőségei.",
    canonicalPath: "/sutik",
  },
  "/aszf": {
    title: "ÁSZF | Folyamat",
    description: "A Folyamat általános szerződési feltételei.",
    canonicalPath: "/aszf",
  },
  "/jogi-kozlemeny": {
    title: "Jogi közlemény | Folyamat",
    description: "Jogi tájékoztató a Folyamat publikus oldalaihoz és szolgáltatásaihoz.",
    canonicalPath: "/jogi-kozlemeny",
  },
  "/akadalymentesites": {
    title: "Akadálymentesítési nyilatkozat | Folyamat",
    description: "A Folyamat webhely akadálymentesítési elkötelezettsége és ismert korlátai.",
    canonicalPath: "/akadalymentesites",
  },
  "/visszaterites": {
    title: "Visszatérítés | Folyamat",
    description: "Visszatérítési és lemondási feltételek a Folyamat szolgáltatásaihoz.",
    canonicalPath: "/visszaterites",
  },
  "/hirlevel": {
    title: "Hírlevél | Folyamat",
    description: "Feliratkozás a Folyamat hírlevelére.",
    canonicalPath: "/hirlevel",
  },
  "/bojt": {
    title: "Bibliai Böjt | Folyamat",
    description:
      "Mélyebb ismeret a böjtről, majd komplett bibliai gyakorlat Isten felé.",
    canonicalPath: "/bojt",
    robots: NOINDEX_ROBOTS,
  },
  "/hirlevel/sikeres-feliratkozas": {
    title: "Sikeres feliratkozás | Folyamat",
    description: "A hírlevél-feliratkozásod sikeresen megerősítve.",
    canonicalPath: "/hirlevel/sikeres-feliratkozas",
    robots: NOINDEX_ROBOTS,
  },
  "/hirlevel/link-hiba": {
    title: "Hírlevél link hiba | Folyamat",
    description: "A hírlevél megerősítő link nem érvényes vagy lejárt.",
    canonicalPath: "/hirlevel/link-hiba",
    robots: NOINDEX_ROBOTS,
  },
  "/auth": {
    title: "Belépés | Folyamat",
    description: "Belépés a Folyamat felületére.",
    canonicalPath: "/auth",
    robots: NOINDEX_ROBOTS,
  },
  "/login": {
    title: "Belépés | Folyamat",
    description: "Belépés a Folyamat felületére.",
    canonicalPath: "/auth",
    robots: NOINDEX_ROBOTS,
  },
  "/register": {
    title: "Regisztráció | Folyamat",
    description: "Fiók létrehozása a Folyamat felületére.",
    canonicalPath: "/auth",
    robots: NOINDEX_ROBOTS,
  },
  "/auth/callback": {
    title: "Belépés | Folyamat",
    description: "Belépés a Folyamat felületére.",
    canonicalPath: "/auth",
    robots: NOINDEX_ROBOTS,
  },
  "/reset-password": {
    title: "Jelszó visszaállítás | Folyamat",
    description: "Új jelszó beállítása a Folyamat fiókodhoz.",
    canonicalPath: "/reset-password",
    robots: NOINDEX_ROBOTS,
  },
  "/start": {
    ...HOME_METADATA,
    canonicalPath: "/start",
  },
  "/coming-soon": {
    title: "Hamarosan | Folyamat",
    description: "Értesülj a Folyamat következő indulásáról.",
    canonicalPath: "/coming-soon",
    robots: NOINDEX_ROBOTS,
  },
  "/404": {
    title: "Oldal nem található | Folyamat",
    description: "Ez az oldal nem létezik vagy áthelyeztük.",
    canonicalPath: "/404",
    robots: NOINDEX_ROBOTS,
  },
  "/nem-elerheto": {
    title: "Oldal nem elérhető | Folyamat",
    description: "Ez az oldal most nem elérhető.",
    canonicalPath: "/nem-elerheto",
    robots: NOINDEX_ROBOTS,
  },
  "/system/nem-elerheto": {
    title: "Oldal nem elérhető | Folyamat",
    description: "Ez az oldal most nem elérhető.",
    canonicalPath: "/nem-elerheto",
    robots: NOINDEX_ROBOTS,
  },
  "/tamogatas": {
    title: "Támogatás | Folyamat",
    description:
      "Hozzájárulás a Folyamat munkájához, hogy mások számára is elérhető maradjon.",
    canonicalPath: "/tamogatas",
  },
  "/csatlakozas": {
    title: "Csatlakozás | Folyamat",
    description: FALLBACK_METADATA.description,
    canonicalPath: "/csatlakozas",
    robots: NOINDEX_ROBOTS,
  },
  "/mentor/kreditek": {
    title: "Mentor kreditek | Folyamat",
    description: "Mentor kreditek kezelése.",
    canonicalPath: "/mentor/kreditek",
    robots: NOINDEX_ROBOTS,
  },
  "/terv/edukativ-feed": {
    title: "Feed tervezet | Folyamat",
    description: "Belső feed prototípus.",
    canonicalPath: "/terv/edukativ-feed",
    robots: NOINDEX_ROBOTS,
  },
  "/events/2026-04-18-egyeni-kezeles": {
    title: "Forrás-beszélgetés | Folyamat",
    description: "Ingyenes személyes beszélgetés a visszatérés útján.",
    canonicalPath: "/events/2026-04-18-egyeni-kezeles",
  },
  "/events/2026-04-19-egyeni-kezeles": {
    title: "Forrás-beszélgetés | Folyamat",
    description: "Ingyenes személyes beszélgetés a visszatérés útján.",
    canonicalPath: "/events/2026-04-19-egyeni-kezeles",
  },
  "/events/2026-04-18-egyeni-kezeles/success": {
    title: "Sikeres foglalás | Folyamat",
    description: "A foglalás részleteit e-mailben küldjük.",
    canonicalPath: "/events/2026-04-18-egyeni-kezeles/success",
    robots: NOINDEX_ROBOTS,
  },
  "/events/2026-04-19-egyeni-kezeles/success": {
    title: "Sikeres foglalás | Folyamat",
    description: "A foglalás részleteit e-mailben küldjük.",
    canonicalPath: "/events/2026-04-19-egyeni-kezeles/success",
    robots: NOINDEX_ROBOTS,
  },
};

export const STATIC_ROUTE_SEO_PATHS = Object.freeze(Object.keys(STATIC_ROUTE_METADATA));

export const getStaticRouteSeoDefinition = (
  pathname: string,
): PartialSeoMetadata | undefined => STATIC_ROUTE_METADATA[normalizePathname(pathname)];

const ROUTE_MATCHERS: RouteMatcher[] = [
  {
    test: /^\/events-app\/[^/]+$/,
    resolve: (pathname) => ({
      title: "Esemény | Folyamat",
      description: "Technikai esemény előnézet.",
      canonicalPath: pathname.replace(EVENT_APP_PATH_PREFIX, EVENT_PATH_PREFIX),
      robots: NOINDEX_ROBOTS,
    }),
  },
  {
    test: /^\/events\/[^/]+\/success$/,
    resolve: (pathname) => ({
      title: "Sikeres jelentkezés | Folyamat",
      description: "A sikeres fizetés részleteit e-mailben küldjük.",
      canonicalPath: pathname,
      robots: NOINDEX_ROBOTS,
    }),
  },
  {
    test: /^\/p\/[^/]+$/,
    resolve: (pathname) => ({
      title: "Cikk | Folyamat",
      description: FALLBACK_METADATA.description,
      canonicalPath: pathname,
      robots: INDEX_ROBOTS,
      type: "article",
    }),
  },
  {
    test: /^\/events\/[^/]+$/,
    resolve: (pathname) => ({
      title: "Esemény | Folyamat",
      description: "Részletek a Folyamat egyik publikus eseményéről.",
      canonicalPath: pathname,
      robots: INDEX_ROBOTS,
      type: "event",
    }),
  },
  {
    test: /^\/courses\/[^/]+$/,
    resolve: (pathname) => ({
      title: "Tanfolyam | Folyamat",
      description: "Részletek a Folyamat egyik publikus tanfolyamáról.",
      canonicalPath: pathname,
      robots: NOINDEX_ROBOTS,
    }),
  },
  {
    test: /^\/blog\/[^/]+$/,
    resolve: (pathname) => ({
      title: "Cikk | Folyamat",
      description: FALLBACK_METADATA.description,
      canonicalPath: pathname.replace(/^\/blog\//, BLOG_POST_PATH_PREFIX),
      robots: NOINDEX_ROBOTS,
      type: "article",
    }),
  },
  {
    test: /^\/post\/[^/]+$/,
    resolve: (pathname) => ({
      title: "Cikk | Folyamat",
      description: FALLBACK_METADATA.description,
      canonicalPath: pathname.replace(/^\/post\//, BLOG_POST_PATH_PREFIX),
      robots: NOINDEX_ROBOTS,
      type: "article",
    }),
  },
  {
    test: /^\/(profile|profilom)(?:\/.*)?$/,
    resolve: (pathname) => ({
      title: "Fiók | Folyamat",
      description: "Fiók- és előfizetéskezelés.",
      canonicalPath: pathname.startsWith("/profile") ? "/profilom" : pathname,
      robots: NOINDEX_ROBOTS,
    }),
  },
  {
    test: /^\/(folyamataim|kedvenceim|offline|jegyzeteim|conversations)$/,
    resolve: (pathname) => ({
      title: "Belső oldal | Folyamat",
      description: FALLBACK_METADATA.description,
      canonicalPath: pathname,
      robots: NOINDEX_ROBOTS,
    }),
  },
  {
    test: /^\/anyagok\/[^/]+$/,
    resolve: (pathname) => ({
      title: "Tananyag | Folyamat",
      description: FALLBACK_METADATA.description,
      canonicalPath: pathname,
      robots: NOINDEX_ROBOTS,
    }),
  },
  {
    test: /^\/aramlas\/home\/?$/,
    resolve: (pathname) => ({
      title: "Archívum | Folyamat",
      ogTitle: "Archívum | Folyamat",
      description: "Az Archívum hang- és videótartalmai egy helyen.",
      ogDescription: "Az Archívum hang- és videótartalmai egy helyen.",
      canonicalPath: pathname,
      robots: NOINDEX_ROBOTS,
    }),
  },
  {
    test: /^\/aramlas\/nincs-hozzaferes\/?$/,
    resolve: (pathname) => ({
      title: "Archívum tagság szükséges | Folyamat",
      ogTitle: "Archívum tagság szükséges | Folyamat",
      description: "A tartalom csak aktív Archívum tagsággal érhető el.",
      ogDescription: "A tartalom csak aktív Archívum tagsággal érhető el.",
      canonicalPath: pathname,
      robots: NOINDEX_ROBOTS,
    }),
  },
  {
    test: /^\/aramlas(?:\/.*)?$/,
    resolve: (pathname) => ({
      title: "Áramlás | Folyamat",
      description: FALLBACK_METADATA.description,
      canonicalPath: pathname,
      robots: NOINDEX_ROBOTS,
    }),
  },
  {
    test: /^\/s\/[^/]+$/,
    resolve: (pathname) => ({
      title: FALLBACK_METADATA.title,
      description: FALLBACK_METADATA.description,
      canonicalPath: pathname,
      robots: NOINDEX_ROBOTS,
    }),
  },
  {
    test: /^\/chat$/,
    resolve: () => ({
      title: "Mentor | Folyamat",
      description:
        "A Mentor segít eligazodni a Folyamat tartalmaiban és megtalálni a következő tiszta lépést.",
      canonicalPath: "/mentor",
      robots: NOINDEX_ROBOTS,
    }),
  },
];

const sanitizeText = (value: string | null | undefined): string =>
  (value || "")
    .replace(/\s+/g, " ")
    .replace(/[<>]/g, "")
    .trim();

const normalizePathname = (pathname: string): string => {
  if (!pathname) return "/";
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
};

export const isPrivateAdminSeoPath = (pathname: string): boolean => {
  const normalizedPath = normalizePathname(pathname);
  return PRIVATE_ADMIN_SEO_PREFIXES.some(
    (prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`),
  );
};

const formatDateHu = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat("hu-HU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(parsed);
};

const trimDescription = (value: string): string => {
  const normalized = sanitizeText(value);
  if (normalized.length <= DESCRIPTION_LIMIT) return normalized;
  const clipped = normalized.slice(0, DESCRIPTION_LIMIT - 1);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${(lastSpace > 90 ? clipped.slice(0, lastSpace) : clipped).trim()}…`;
};

const withBrand = (title: string): string => {
  const normalized = sanitizeText(title);
  if (!normalized) return FALLBACK_METADATA.title;
  if (normalized.endsWith(BRAND_SUFFIX)) return normalized;
  return normalized.length + BRAND_SUFFIX.length <= TITLE_BRAND_LIMIT
    ? `${normalized}${BRAND_SUFFIX}`
    : normalized;
};

const ensureLeadingSlash = (path: string): string => {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

export const buildCanonicalUrl = (path: string): string => {
  if (!path) return CANONICAL_SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = ensureLeadingSlash(path);
  return `${CANONICAL_SITE_URL}${normalizedPath === "/" ? "" : normalizedPath}`;
};

export const resolveOgImageUrl = (imageUrl: string | null | undefined): string => {
  const normalized = sanitizeText(imageUrl);
  if (!normalized) return buildCanonicalUrl(DEFAULT_OG_IMAGE_PATH);
  if (/^https?:\/\//i.test(normalized)) return normalized;
  return buildCanonicalUrl(normalized);
};

const finalizeMetadata = (metadata: PartialSeoMetadata): SeoMetadata => {
  const title = sanitizeText(metadata.title) || FALLBACK_METADATA.title;
  const description = trimDescription(metadata.description || FALLBACK_METADATA.description);
  const ogTitle = sanitizeText(metadata.ogTitle) || title;
  const ogDescription = trimDescription(metadata.ogDescription || description);

  return {
    title,
    description,
    ogTitle,
    ogDescription,
    canonicalPath: ensureLeadingSlash(metadata.canonicalPath || "/start"),
    imageUrl: resolveOgImageUrl(metadata.imageUrl),
    robots: metadata.robots || INDEX_ROBOTS,
    type: metadata.type || "website",
    twitterCard: "summary_large_image",
    publishedTime: metadata.publishedTime,
  };
};

const mergeRouteMetadata = (partial: PartialSeoMetadata): SeoMetadata =>
  // Never spread FALLBACK_METADATA ogTitle/ogDescription here: they would override
  // route-specific title/description and leak global share copy into route shells.
  finalizeMetadata({
    title: FALLBACK_METADATA.title,
    description: FALLBACK_METADATA.description,
    ...partial,
  });

const withPublicIndexPolicy = (pathname: string, metadata: SeoMetadata): SeoMetadata => {
  const allowedRobots = resolvePublicRobots(pathname);
  if (allowedRobots === NOINDEX_ROBOTS && metadata.robots !== NOINDEX_ROBOTS) {
    return { ...metadata, robots: NOINDEX_ROBOTS };
  }
  return metadata;
};

export const resolveRouteSeo = (pathname: string): SeoMetadata => {
  const normalizedPath = normalizePathname(pathname);
  const exactMetadata = STATIC_ROUTE_METADATA[normalizedPath];

  if (exactMetadata) {
    return withPublicIndexPolicy(normalizedPath, mergeRouteMetadata(exactMetadata));
  }

  for (const matcher of ROUTE_MATCHERS) {
    if (matcher.test.test(normalizedPath)) {
      return withPublicIndexPolicy(normalizedPath, mergeRouteMetadata(matcher.resolve(normalizedPath)));
    }
  }

  if (isPrivateAdminSeoPath(normalizedPath)) {
    return withPublicIndexPolicy(
      normalizedPath,
      finalizeMetadata({
        ...ADMIN_APP_METADATA,
        canonicalPath: normalizedPath,
        robots: NOINDEX_ROBOTS,
      }),
    );
  }

  if (
    normalizedPath.startsWith(PROFILE_PATH_PREFIX) ||
    normalizedPath.startsWith(ARAMLAS_PATH_PREFIX)
  ) {
    return withPublicIndexPolicy(
      normalizedPath,
      finalizeMetadata({
        ...FALLBACK_METADATA,
        canonicalPath: normalizedPath,
        robots: NOINDEX_ROBOTS,
      }),
    );
  }

  return withPublicIndexPolicy(
    normalizedPath,
    finalizeMetadata({
      ...FALLBACK_METADATA,
      canonicalPath: normalizedPath,
      robots: NOINDEX_ROBOTS,
    }),
  );
};

export const buildPostSeo = (input: PostSeoInput): SeoMetadata =>
  finalizeMetadata({
    title: withBrand(input.title),
    description: input.excerpt || input.metaDescription || FALLBACK_METADATA.description,
    ogTitle: withBrand(input.title),
    ogDescription: input.excerpt || input.metaDescription || FALLBACK_METADATA.ogDescription,
    canonicalPath: `${BLOG_POST_PATH_PREFIX}${input.slug}`,
    imageUrl: input.imageUrl,
    robots: INDEX_ROBOTS,
    type: "article",
    publishedTime: input.publishedAt || undefined,
  });

export const buildEventSeo = (input: EventSeoInput): SeoMetadata => {
  const dateLabel = formatDateHu(input.eventDate);
  const descriptionParts = [dateLabel, sanitizeText(input.description)].filter(Boolean);
  const description = descriptionParts.join(" - ") || FALLBACK_METADATA.description;

  return finalizeMetadata({
    title: withBrand(input.title),
    description,
    ogTitle: withBrand(input.title),
    ogDescription: description,
    canonicalPath: input.canonicalPath || `${EVENT_PATH_PREFIX}${input.slugOrId}`,
    imageUrl: input.imageUrl,
    robots: INDEX_ROBOTS,
    type: "event",
  });
};

export const buildCourseSeo = (input: CourseSeoInput): SeoMetadata => {
  const description = input.shortDescription || input.description || "Publikus tanfolyam oldal a Folyamaton.";

  return finalizeMetadata({
    title: withBrand(input.title),
    description,
    ogTitle: withBrand(input.title),
    ogDescription: description,
    canonicalPath: `${COURSE_PATH_PREFIX}${input.slug}`,
    imageUrl: input.imageUrl,
    robots: INDEX_ROBOTS,
  });
};
